// sampleGeoidCog.js
//
// Browser-side geoid sampler that reads geoid undulation N(lon, lat) from a
// hosted Cloud Optimized GeoTIFF (COG) using geotiff.js.
//
// Design notes:
// -------------
// - COG (Cloud Optimized GeoTIFF) enables efficient partial HTTP range reads.
//   The file is structured with internal tiling and overviews, allowing clients
//   to fetch only the bytes needed for a specific region without downloading
//   the entire file. This is critical for large geoid grids (e.g., 1GB+).
//
// - This module assumes the COG is georeferenced in geographic coordinates
//   (EPSG:4326 or similar lon/lat in degrees). This enables simple affine
//   pixel mapping: pixel_x = (lon - minLon) / pixelWidth. More complex
//   projections would require proj4 transformation before sampling.
//
// - NoData handling: Geoid grids often use sentinel values (e.g., -32768 or
//   -9999) for ocean/invalid areas. We detect these and return an error rather
//   than interpolating with invalid data.
//
// Limitations:
// - Single-point sampling only (no batch/tile extraction)
// - Assumes north-up raster (positive Y = north, standard for geographic CRS)
// - Assumes band 1 contains the undulation value in meters
// - No support for rotated or skewed rasters

import { fromUrl } from 'geotiff';

/**
 * Error codes returned by sampleGeoidN
 * @readonly
 * @enum {string}
 */
export const ErrorCodes = {
  /** Coordinates are outside the geoid grid bounding box */
  OUT_OF_BOUNDS: 'OUT_OF_BOUNDS',
  /** Sampled pixel(s) contain NoData or NaN values */
  NO_DATA: 'NO_DATA',
  /** Network error fetching the COG */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** Error from geotiff.js parsing or reading */
  GEOTIFF_ERROR: 'GEOTIFF_ERROR',
  /** Invalid input parameters */
  INVALID_INPUT: 'INVALID_INPUT',
  /** Request timed out */
  TIMEOUT: 'TIMEOUT',
};

/**
 * In-memory cache for opened GeoTIFF handles.
 * Maps URL -> Promise<{ tiff, image, noDataValue, bbox, width, height }>
 * Using Promise allows concurrent requests to share the same opening process.
 * @type {Map<string, Promise<Object>>}
 */
const tiffCache = new Map();

/**
 * Open a COG and get the first image with metadata.
 * Results are cached by URL.
 *
 * @param {string} url - COG URL
 * @param {Object} options - Options
 * @param {boolean} [options.cache=true] - Use cache
 * @param {number} [options.timeoutMs] - Timeout in milliseconds
 * @returns {Promise<{ tiff: Object, image: Object, noDataValue: number|null, bbox: number[], width: number, height: number }>}
 */
async function openCog(url, options = {}) {
  const { cache = true, timeoutMs } = options;

  // Check cache first
  if (cache && tiffCache.has(url)) {
    return tiffCache.get(url);
  }

  // Create the opening promise
  const openPromise = (async () => {
    // Set up abort controller for timeout
    let controller;
    let timeoutId;

    const fetchOptions = {};
    if (timeoutMs && timeoutMs > 0) {
      controller = new AbortController();
      fetchOptions.signal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    }

    try {
      const tiff = await fromUrl(url, fetchOptions);
      const image = await tiff.getImage();

      // Get nodata value from metadata
      const fileDirectory = image.fileDirectory;
      let noDataValue = null;

      // GDALNoData is stored as a string in the TIFF tag
      if (fileDirectory.GDAL_NODATA !== undefined) {
        noDataValue = parseFloat(fileDirectory.GDAL_NODATA);
      }

      // Get bounding box and dimensions
      const bbox = image.getBoundingBox(); // [minX, minY, maxX, maxY] = [minLon, minLat, maxLon, maxLat]
      const width = image.getWidth();
      const height = image.getHeight();

      return { tiff, image, noDataValue, bbox, width, height };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  })();

  // Cache the promise (not the result) to handle concurrent requests
  if (cache) {
    tiffCache.set(url, openPromise);

    // Remove from cache on error
    openPromise.catch(() => {
      tiffCache.delete(url);
    });
  }

  return openPromise;
}

/**
 * Convert lon/lat to pixel coordinates.
 *
 * @param {number} lon - Longitude in degrees
 * @param {number} lat - Latitude in degrees
 * @param {number[]} bbox - [minLon, minLat, maxLon, maxLat]
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @returns {{ px: number, py: number }} Pixel coordinates (can be fractional)
 */
function lonLatToPixel(lon, lat, bbox, width, height) {
  const [minLon, minLat, maxLon, maxLat] = bbox;

  // Pixel size in degrees
  const pixelWidth = (maxLon - minLon) / width;
  const pixelHeight = (maxLat - minLat) / height;

  // Convert to pixel coordinates
  // Note: For north-up rasters, Y increases downward in pixel space
  const px = (lon - minLon) / pixelWidth;
  const py = (maxLat - lat) / pixelHeight; // Flip Y axis

  return { px, py };
}

/**
 * Check if a value is NoData.
 *
 * @param {number} value - Value to check
 * @param {number|null} noDataValue - NoData sentinel value
 * @returns {boolean} True if value is NoData or invalid
 */
function isNoData(value, noDataValue) {
  if (!Number.isFinite(value)) return true;
  if (noDataValue !== null && value === noDataValue) return true;
  // Common nodata values
  if (value === -32768 || value === -9999 || value === -99999) return true;
  return false;
}

/**
 * Perform bilinear interpolation on a 2x2 grid.
 *
 * @param {number} v00 - Value at (0,0) - top-left
 * @param {number} v10 - Value at (1,0) - top-right
 * @param {number} v01 - Value at (0,1) - bottom-left
 * @param {number} v11 - Value at (1,1) - bottom-right
 * @param {number} fx - Fractional X position [0,1]
 * @param {number} fy - Fractional Y position [0,1]
 * @returns {number} Interpolated value
 */
function bilinearInterpolate(v00, v10, v01, v11, fx, fy) {
  const top = v00 * (1 - fx) + v10 * fx;
  const bottom = v01 * (1 - fx) + v11 * fx;
  return top * (1 - fy) + bottom * fy;
}

/**
 * @typedef {Object} SampleOptions
 * @property {number} [windowPixels=1] - Window size for sampling (1 = 2x2 bilinear)
 * @property {boolean} [preferOverview=true] - Allow geotiff.js to use overviews
 * @property {number} [timeoutMs] - Timeout for fetch operations in milliseconds
 * @property {boolean} [cache=true] - Cache opened TIFF handles
 * @property {boolean} [debug=false] - Include raw pixel values and interpolation details in result
 */

/**
 * @typedef {Object} SampleSource
 * @property {string} url - COG URL used
 * @property {number} [usedOverview] - Overview level used (if applicable)
 * @property {number[]} bbox - Bounding box [minLon, minLat, maxLon, maxLat]
 * @property {{ x: number, y: number }} pixel - Pixel coordinates sampled
 */

/**
 * @typedef {Object} SampleSuccess
 * @property {true} ok - Success indicator
 * @property {number} N - Geoid undulation value in meters
 * @property {"m"} unit - Unit of measurement
 * @property {SampleSource} source - Source information
 * @property {string[]} warnings - Any warnings generated
 */

/**
 * @typedef {Object} SampleError
 * @property {false} ok - Failure indicator
 * @property {{ code: string, message: string }} error - Error details
 * @property {string[]} warnings - Any warnings generated before failure
 */

/**
 * Sample geoid undulation N at a given longitude/latitude from a COG.
 *
 * The COG must be:
 * - Georeferenced in geographic coordinates (EPSG:4326 or similar)
 * - Band 1 contains undulation values in meters
 * - Hosted on a server supporting HTTP Range requests
 *
 * @param {string} geoidCogUrl - URL to the Cloud Optimized GeoTIFF
 * @param {number} lon - Longitude in degrees [-180, 180]
 * @param {number} lat - Latitude in degrees [-90, 90]
 * @param {SampleOptions} [options] - Sampling options
 * @returns {Promise<SampleSuccess|SampleError>} Result object
 *
 * @example
 * const result = await sampleGeoidN('https://example.com/geoid.tif', 139.7, 35.6);
 * if (result.ok) {
 *   console.log('Geoid undulation:', result.N, result.unit);
 * } else {
 *   console.error(result.error.code, result.error.message);
 * }
 */
export async function sampleGeoidN(geoidCogUrl, lon, lat, options = {}) {
  const warnings = [];
  const { windowPixels = 1, cache = true, timeoutMs, debug = false } = options;

  // Validate inputs
  if (typeof geoidCogUrl !== 'string' || !geoidCogUrl) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.INVALID_INPUT,
        message: 'geoidCogUrl must be a non-empty string.',
      },
      warnings,
    };
  }

  if (typeof lon !== 'number' || typeof lat !== 'number' ||
      !Number.isFinite(lon) || !Number.isFinite(lat)) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.INVALID_INPUT,
        message: `Invalid coordinates: lon=${lon}, lat=${lat}. Both must be finite numbers.`,
      },
      warnings,
    };
  }

  // Warn if coordinates are outside typical ranges
  if (lon < -180 || lon > 180) {
    warnings.push(`Longitude ${lon} is outside [-180, 180]. Proceeding anyway.`);
  }
  if (lat < -90 || lat > 90) {
    warnings.push(`Latitude ${lat} is outside [-90, 90]. Proceeding anyway.`);
  }

  // Open the COG
  let cogData;
  try {
    cogData = await openCog(geoidCogUrl, { cache, timeoutMs });
  } catch (err) {
    // Determine error type
    if (err.name === 'AbortError') {
      return {
        ok: false,
        error: {
          code: ErrorCodes.TIMEOUT,
          message: `Request timed out after ${timeoutMs}ms.`,
        },
        warnings,
      };
    }

    const isNetworkError = err.message?.includes('fetch') ||
                           err.message?.includes('network') ||
                           err.message?.includes('Failed to fetch') ||
                           err.name === 'TypeError';

    return {
      ok: false,
      error: {
        code: isNetworkError ? ErrorCodes.NETWORK_ERROR : ErrorCodes.GEOTIFF_ERROR,
        message: `Failed to open COG: ${err.message || String(err)}`,
      },
      warnings,
    };
  }

  const { image, noDataValue, bbox, width, height } = cogData;

  // Check if coordinates are within bounds
  const [minLon, minLat, maxLon, maxLat] = bbox;
  if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.OUT_OF_BOUNDS,
        message: `Coordinates (${lon}, ${lat}) are outside grid bounds [${minLon}, ${minLat}] to [${maxLon}, ${maxLat}].`,
      },
      warnings,
    };
  }

  // Convert to pixel coordinates
  const { px, py } = lonLatToPixel(lon, lat, bbox, width, height);

  // Calculate window for sampling
  let x0 = Math.floor(px);
  let y0 = Math.floor(py);
  let x1 = x0 + windowPixels;
  let y1 = y0 + windowPixels;

  // Clamp to image bounds
  const originalX0 = x0, originalY0 = y0, originalX1 = x1, originalY1 = y1;

  x0 = Math.max(0, x0);
  y0 = Math.max(0, y0);
  x1 = Math.min(width - 1, x1);
  y1 = Math.min(height - 1, y1);

  const windowWidth = x1 - x0 + 1;
  const windowHeight = y1 - y0 + 1;

  const wasClamped = x0 !== originalX0 || y0 !== originalY0 ||
                     x1 !== originalX1 || y1 !== originalY1;

  if (wasClamped) {
    warnings.push(`Sampling window clamped at image edge. Original: [${originalX0},${originalY0}]-[${originalX1},${originalY1}], Clamped: [${x0},${y0}]-[${x1},${y1}].`);
  }

  // Read raster data
  let rasterData;
  try {
    // geotiff.js window is [left, top, right, bottom] (exclusive end)
    const result = await image.readRasters({
      window: [x0, y0, x1 + 1, y1 + 1],
      samples: [0], // Band 1
      interleave: true,
    });
    rasterData = result;
  } catch (err) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.GEOTIFF_ERROR,
        message: `Failed to read raster data: ${err.message || String(err)}`,
      },
      warnings,
    };
  }

  // Extract values from the window
  const getValue = (dx, dy) => {
    const idx = dy * windowWidth + dx;
    return rasterData[idx];
  };

  // Check for single-pixel fallback (nearest neighbor)
  if (windowWidth === 1 && windowHeight === 1) {
    const value = getValue(0, 0);

    if (isNoData(value, noDataValue)) {
      return {
        ok: false,
        error: {
          code: ErrorCodes.NO_DATA,
          message: `Sampled pixel at (${x0}, ${y0}) contains NoData value.`,
        },
        warnings,
      };
    }

    warnings.push('Using nearest-neighbor sampling (single pixel at edge).');

    return {
      ok: true,
      N: value,
      unit: 'm',
      source: {
        url: geoidCogUrl,
        bbox,
        pixel: { x: x0, y: y0 },
      },
      warnings,
    };
  }

  // Get 2x2 values for bilinear interpolation
  // Handle case where window might be smaller due to clamping
  const v00 = getValue(0, 0);
  const v10 = windowWidth > 1 ? getValue(1, 0) : v00;
  const v01 = windowHeight > 1 ? getValue(0, 1) : v00;
  const v11 = (windowWidth > 1 && windowHeight > 1) ? getValue(1, 1) : v00;

  // Check for NoData in any corner
  const values = [v00, v10, v01, v11];
  const noDataCorners = values.filter(v => isNoData(v, noDataValue));

  if (noDataCorners.length === 4) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.NO_DATA,
        message: `All sampled pixels contain NoData values.`,
      },
      warnings,
    };
  }

  if (noDataCorners.length > 0) {
    // Some corners are NoData - fall back to nearest valid neighbor
    const validValues = values.filter(v => !isNoData(v, noDataValue));
    const nearestValue = validValues[0];

    warnings.push(`${noDataCorners.length} of 4 pixels contain NoData. Using nearest valid neighbor.`);

    return {
      ok: true,
      N: nearestValue,
      unit: 'm',
      source: {
        url: geoidCogUrl,
        bbox,
        pixel: { x: Math.round(px), y: Math.round(py) },
      },
      warnings,
    };
  }

  // Perform bilinear interpolation
  const fx = px - x0; // Fractional X within the window
  const fy = py - y0; // Fractional Y within the window

  const N = bilinearInterpolate(v00, v10, v01, v11, fx, fy);

  const result = {
    ok: true,
    N,
    unit: 'm',
    source: {
      url: geoidCogUrl,
      bbox,
      pixel: { x: px, y: py },
    },
    warnings,
  };

  // Include debug information if requested
  if (debug) {
    result.debug = {
      // Image metadata
      imageSize: { width, height },
      pixelSize: {
        lon: (maxLon - minLon) / width,
        lat: (maxLat - minLat) / height,
      },
      // Exact pixel coordinates
      pixelCoords: { px, py },
      pixelInt: { x0, y0 },
      // Fractional position for interpolation
      fractional: { fx, fy },
      // Raw 2x2 pixel values (before interpolation)
      rawValues: {
        v00, // top-left
        v10, // top-right
        v01, // bottom-left
        v11, // bottom-right
      },
      // Nearest neighbor value (for comparison)
      nearestNeighbor: v00,
      // Interpolation breakdown
      interpolation: {
        topRow: v00 * (1 - fx) + v10 * fx,
        bottomRow: v01 * (1 - fx) + v11 * fx,
        final: N,
      },
    };
  }

  return result;
}

/**
 * Clear the COG cache.
 * Useful for freeing memory or forcing reload of updated files.
 *
 * @param {string} [url] - Specific URL to clear. If omitted, clears entire cache.
 */
export function clearCache(url) {
  if (url) {
    tiffCache.delete(url);
  } else {
    tiffCache.clear();
  }
}

/**
 * Get cache statistics.
 *
 * @returns {{ size: number, urls: string[] }} Cache info
 */
export function getCacheStats() {
  return {
    size: tiffCache.size,
    urls: [...tiffCache.keys()],
  };
}

// ============================================================================
// Example usage:
// ============================================================================
//
// import { sampleGeoidN } from './sampleGeoidCog';
//
// const JP_GEOID_URL = 'https://your-bucket.s3.amazonaws.com/gsigeo2011.tif';
//
// async function getGeoidUndulation(lon, lat) {
//   const r = await sampleGeoidN(JP_GEOID_URL, lon, lat);
//
//   if (!r.ok) {
//     console.error(r.error.code, r.error.message);
//     return null;
//   }
//
//   if (r.warnings.length > 0) {
//     console.warn('Warnings:', r.warnings);
//   }
//
//   console.log('N =', r.N, r.unit);
//   return r.N;
// }
//
// // Example: Convert ellipsoidal height to orthometric height
// async function ellipsoidalToOrthometric(lon, lat, h) {
//   const N = await getGeoidUndulation(lon, lat);
//   if (N === null) return null;
//   return h - N; // H = h - N
// }
